import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  type CredentialImplementationForStaffQuery,
  CredentialVerificationStatus,
} from "generated-graphql/graphql";
import { prettyPrintDateTimeMed } from "util/dates";

import { DrawerSection, drawerSectionStackSpacing, drawerTwoColumnGridSx } from "./DrawerSection";

/** Tooltip copy reused on the grid `Facility in Portal` column header — same string keeps grid + drawer in sync. */
const FACILITY_IN_PORTAL_TOOLTIP =
  "Indicates whether this facility's state ID was seen in the portal during the last credential verification check";

type Row = NonNullable<CredentialImplementationForStaffQuery["credentialImplementationForStaff"]>;

function InfoCell({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {tooltip ? (
          <Tooltip title={tooltip} placement="top">
            <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
          </Tooltip>
        ) : null}
      </Stack>
      {children}
    </Box>
  );
}

export function RowSummary({ row }: { row: Row }) {
  const csmDisplay =
    row.csmName || row.csmEmail
      ? `${row.csmName ?? row.csmEmail}${row.csmName && row.csmEmail ? ` (${row.csmEmail})` : ""}`
      : "—";

  return (
    <Stack spacing={drawerSectionStackSpacing} divider={<Divider flexItem />}>
      <DrawerSection title="Facility">
        <Box sx={drawerTwoColumnGridSx}>
          <InfoCell label="Tenant">
            <Typography variant="body2">{row.tenantName}</Typography>
          </InfoCell>
          <InfoCell label="Portal">
            <Typography variant="body2">
              {row.portalJurisdiction} ({row.portalName})
            </Typography>
          </InfoCell>
          <InfoCell label="Facility">
            <Typography variant="body2">{row.facilityName ?? "—"}</Typography>
          </InfoCell>
          <InfoCell label="State / County">
            <Typography variant="body2">
              {[row.state, row.county].filter(Boolean).join(" · ") || "—"}
            </Typography>
          </InfoCell>
          <InfoCell label="Address">
            <Typography variant="body2">{row.facilityAddress ?? "—"}</Typography>
          </InfoCell>
          <InfoCell label="CSM">
            <Typography variant="body2">{csmDisplay}</Typography>
          </InfoCell>
        </Box>
      </DrawerSection>

      <DrawerSection title="Credential">
        <Box sx={drawerTwoColumnGridSx}>
          <InfoCell label="Credential (login)">
            <Typography variant="body2">{row.credentialName ?? "—"}</Typography>
          </InfoCell>
          <InfoCell label="Credential email">
            <Typography variant="body2">{row.credentialEmail ?? "—"}</Typography>
          </InfoCell>
          <InfoCell label="Credential in Encamp">
            <Typography variant="body2">{row.credentialInEncamp ? "Yes" : "No"}</Typography>
          </InfoCell>
          <InfoCell label="Credential status">
            <Typography variant="body2">
              {row.credentialStatus
                ? prettyPrintEnumValue(
                    row.credentialStatus as CredentialVerificationStatus,
                    [],
                    true
                  )
                : "—"}
            </Typography>
          </InfoCell>
          <InfoCell label="State ID">
            <Typography variant="body2">
              {[row.stateIdType, row.stateId].filter(Boolean).join(" ") || "—"}
            </Typography>
          </InfoCell>
          <InfoCell label="Last verified">
            <Typography variant="body2">
              {row.lastVerifiedAt ? prettyPrintDateTimeMed(row.lastVerifiedAt) : "—"}
            </Typography>
          </InfoCell>
          <InfoCell label="Facility in Portal" tooltip={FACILITY_IN_PORTAL_TOOLTIP}>
            <Typography variant="body2">{row.seenInPortal ? "Yes" : "No"}</Typography>
          </InfoCell>
          {(row.lockHolder || row.lockExpiresAt) && (
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <InfoCell label="Lock (in use)">
                <Typography variant="body2">
                  {row.lockHolder ? `In use by ${row.lockHolder}` : "In use"}
                  {row.lockExpiresAt ? ` · until ${prettyPrintDateTimeMed(row.lockExpiresAt)}` : ""}
                </Typography>
              </InfoCell>
            </Box>
          )}
        </Box>
      </DrawerSection>
    </Stack>
  );
}
